import { useState, useCallback } from "react";
import { tournamentsApi, MatchPair, TournamentUpdateRequest } from "../api";
import type { TournamentDetail, TournamentResultRequest } from "../api/tournaments";
import { useToast } from "../context/ToastContext";

/** TournamentDetail[]を round + block_number でペアにまとめる */
function toMatchPairs(details: TournamentDetail[]): MatchPair[] {
  const blocks = new Map<string, TournamentDetail[]>();
  for (const d of details) {
    const round = d.round ?? 0;
    const bn = d.block_number ?? 0;
    const key = `${round}-${bn}`;
    if (!blocks.has(key)) blocks.set(key, []);
    blocks.get(key)!.push(d);
  }

  const pairs: MatchPair[] = [];

  blocks.forEach((players) => {
    const sorted = [...players].sort((a, b) => (a.cell_number ?? 0) - (b.cell_number ?? 0));
    const p1 = sorted[0];
    const p2 = sorted[1];
    const round = p1?.round ?? p2?.round ?? 0;
    const blockNumber = p1?.block_number ?? p2?.block_number ?? 0;

    pairs.push({
      round,
      blockNumber,
      p1TournamentId: p1?.tournament_id,
      p2TournamentId: p2?.tournament_id,
      p1Name: p1?.member_name ?? "BYE",
      p2Name: p2?.member_name ?? "BYE",
      p1Wins: p1?.wins ?? null,
      p2Wins: p2?.wins ?? null,
    });
  });

  return pairs.sort((a, b) => {
    if (a.round !== b.round) return b.round - a.round;
    return a.blockNumber - b.blockNumber;
  });
}

interface TournamentsState {
  details: TournamentDetail[];
  matches: MatchPair[];
  isLoading: boolean;
  error: string | null;
}

interface FetchTournamentOptions {
  silent?: boolean;
  nocache?: boolean;
}

interface CachedTournamentState {
  details: TournamentDetail[];
  matches: MatchPair[];
}

const tournamentCache = new Map<string, CachedTournamentState>();

export const useTournaments = () => {
  const { showToast } = useToast();
  const [state, setState] = useState<TournamentsState>({
    details: [],
    matches: [],
    isLoading: false,
    error: null,
  });

  const fetchTournament = useCallback(
    async (eventId: number | string, options?: FetchTournamentOptions) => {
      const cacheKey = String(eventId);
      const silent = options?.silent ?? false;
      const nocache = options?.nocache ?? false;

      if (nocache) {
        tournamentCache.delete(cacheKey);
      }

      const cached = tournamentCache.get(cacheKey);

      if (cached && !nocache) {
        setState({
          details: cached.details,
          matches: cached.matches,
          isLoading: false,
          error: null,
        });
      } else if (!silent) {
        setState((s) => ({ ...s, isLoading: true, error: null }));
      }

      try {
        const details = await tournamentsApi.getTournament(eventId);
        const matches = toMatchPairs(details);
        tournamentCache.set(cacheKey, { details, matches });
        setState((s) => ({
          ...s,
          details,
          matches,
          isLoading: false,
          error: null,
        }));
      } catch {
        setState((s) => ({
          ...s,
          isLoading: false,
          error: "イベントの取得に失敗しました",
        }));
      }
    },
    []
  );

  const updateTournament = useCallback(
    async (eventId: number | string, body: TournamentUpdateRequest): Promise<boolean> => {
      try {
        await tournamentsApi.updateTournament(eventId, body);
        await fetchTournament(eventId, { silent: true });
        return true;
      } catch {
        showToast("イベントの更新に失敗しました。", "error");
        return false;
      }
    },
    [showToast, fetchTournament]
  );

  const registerTournamentResult = useCallback(
    async (
      eventId: number | string,
      p1TournamentId: string,
      p2TournamentId: string,
      p1Wins: number,
      p2Wins: number
    ): Promise<boolean> => {
      try {
        const body: TournamentResultRequest = {
          player1: { tournament_id: p1TournamentId, wins: p1Wins },
          player2: { tournament_id: p2TournamentId, wins: p2Wins },
        };
        await tournamentsApi.registerTournamentResult(eventId, body);
        await fetchTournament(eventId, { silent: true });
        showToast("試合結果を登録しました。", "success");
        return true;
      } catch (error) {
        console.error("試合結果登録エラー:", error);
        showToast("試合結果の登録に失敗しました。", "error");
        return false;
      }
    },
    [showToast, fetchTournament]
  );

  return { ...state, fetchTournament, updateTournament, registerTournamentResult };
};
